import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import { useSidebar } from "@/components/layout/AppShell";
import { cn } from "@/lib/utils/cn";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setCredentials, setTheme } from "@/store/slices/userSlice";
import type { ThemePreference, User } from "@/types/domain";
import { ROUTES } from "@/constants";
import { useAuth } from "@/features/auth";
import {
  useAllEventContents,
  usePlatformEventMembers,
} from "@/graphql/hooks/useEvents";
import { useEventsFeature } from "@/features/events";
import {
  useAllUserNotifications,
  useSwitchCompany,
} from "@/graphql/hooks/useUsers";
import type { UserNotification } from "@/features/notifications";
import { setActiveCompany, setCompanies } from "@/store/slices/companySlice";
import { isClubleaDomain } from "@/lib/utils/helper";
import { apolloClient } from "@/lib/apollo/client";
import { ME } from "@/graphql/queries/users";
import ProfileSwitchOptions from "./ProfileSwitchOptions";
import type { PlatformEventMember } from "@/lib/utils/platformEventMembers";
const ScanProfileModal = lazy(() =>
  import("@/features/qr-code").then((mod) => ({
    default: mod.ScanProfileModal,
  })),
);

const NotificationsDropdown = lazy(() =>
  import("@/features/notifications").then((mod) => ({
    default: mod.NotificationsDropdown,
  })),
);

// ─── Icon components ──────────────────────────────────────────────
function BellIcon({ hasDot }: { hasDot?: boolean }) {
  return (
    <span className="relative">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
        <path
          fillRule="evenodd"
          d="M4 8a6 6 0 1 1 12 0c0 1.887.454 3.665 1.257 5.234a.75.75 0 0 1-.515 1.076 32.91 32.91 0 0 1-3.256.508 3.5 3.5 0 0 1-6.972 0 32.903 32.903 0 0 1-3.256-.508.75.75 0 0 1-.515-1.076A11.448 11.448 0 0 0 4 8Zm6 7c-.655 0-1.305-.02-1.95-.057a2 2 0 0 0 3.9 0A49.043 49.043 0 0 1 10 15Z"
          clipRule="evenodd"
        />
      </svg>
      {hasDot && (
        <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
        </span>
      )}
    </span>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10 2a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 2ZM10 15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 15ZM10 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM15.657 5.404a.75.75 0 1 0-1.06-1.06l-1.061 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM6.464 14.596a.75.75 0 1 0-1.06-1.06l-1.06 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM18 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 18 10ZM5 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 5 10ZM14.596 15.657a.75.75 0 0 0 1.06-1.06l-1.06-1.061a.75.75 0 1 0-1.06 1.06l1.06 1.06ZM5.404 6.464a.75.75 0 0 0 1.06-1.06L5.404 4.343a.75.75 0 1 0-1.06 1.06l1.06 1.061Z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M7.455 2.004a.75.75 0 0 1 .26.77 7 7 0 0 0 9.958 7.967.75.75 0 0 1 1.067.853A8.5 8.5 0 1 1 6.647 1.921a.75.75 0 0 1 .808.083Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ─── Icon button wrapper ──────────────────────────────────────────
interface IconButtonProps {
  onClick?: () => void;
  label: string;
  children: React.ReactNode;
  className?: string;
}

function IconButton({ onClick, label, children, className }: IconButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{ color: "var(--event-font-colour, var(--event-header-title))" }}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-lg",
        "transition-colors duration-150 hover:bg-neutral-100/10",
        className,
      )}
    >
      {children}
    </button>
  );
}

// ─── Theme toggle (cycles light → dark → system) ──────────────────
function ThemeToggle() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.user.theme);

  const cycleTheme = useCallback(() => {
    const next: Record<ThemePreference, ThemePreference> = {
      light: "dark",
      dark: "system",
      system: "light",
    };
    dispatch(setTheme(next[theme]));
  }, [dispatch, theme]);

  return (
    <IconButton onClick={cycleTheme} label={`Theme: ${theme}`}>
      {theme === "dark" ? <MoonIcon /> : <SunIcon />}
    </IconButton>
  );
}

// ─── User + company dropdown ──────────────────────────────────────
function UserMenu() {
  const user: User | null = useAppSelector((s) => s.user.currentUser);
  const company = useAppSelector((s) => s.company.activeCompany);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const { currentEvent, platformEventMembers } = useEventsFeature();

  const { switchCompany, loading: switching } = useSwitchCompany();
  const [switchError, setSwitchError] = useState<string | null>(null);
  // console.info("switchCompany", meData);

  const initials = user
    ? `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase()
    : "?";
  const membership = user?.selectedCompanyMembership as
    { avatar?: string } | undefined;
  const eventMember: PlatformEventMember | undefined = Array.isArray(platformEventMembers)
    ? platformEventMembers.find((member: PlatformEventMember) =>
        member.userData?.userId === user?.id)
    : undefined;
  const personalSelected = eventMember?.userData?.selectedProfile
    ? eventMember.userData.selectedProfile.type === "PERSONAL"
    : user?.personalProfile === true;
  const personalAvatar = typeof user?.avatar === "string" ? user.avatar : user?.avatarUrl ?? null;
  const menuAvatar = personalSelected ? personalAvatar : membership?.avatar;

  const isLeagueEvent = String(currentEvent?.platformEventType ?? "").includes("LEAGUE");

  const handleSwitchCompany = useCallback(
    async (companyId: string | null) => {
      if (!user || switching) return;
      setSwitchError(null);
      const eventID = currentEvent?.id;
      if (!eventID) {
        setSwitchError("Open an event before switching profiles.");
        return;
      }
      if (isLeagueEvent) {
        setSwitchError("Profile switching is disabled for league events.");
        return;
      }

      try {
        const result = await switchCompany({
          id: companyId ?? user.id,
          ...(eventID ? { eventID } : {}),
          ...(companyId === null ? { personalProfile: true } : {}),
        });
        const response: unknown = result?.data?.switchCompany;
        if (!response) throw new Error("Failed to switch profile");
        if (typeof response === "object" && "error" in response) {
          throw new Error(`Failed to switch profile: ${String(response.error)}`);
        }
        await apolloClient.clearStore();
        const meData = await apolloClient.query({
          query: ME,
          fetchPolicy: "network-only",
          errorPolicy: "none",
        });

        if (!meData.data?.me) throw new Error("Failed to load the switched profile");
        dispatch(setCredentials({ user: meData.data.me }));
        dispatch(setActiveCompany(meData.data.me?.company ?? null));
        dispatch(setCompanies(meData.data.me?.companies ?? []));
        setOpen(false);
        // void navigate(ROUTES.DASHBOARD);
        window.location.reload(); // Refresh the page to apply the new company context
      } catch (error) {
        console.error("Error switching company:", error);
        setSwitchError(error instanceof Error ? error.message : "Failed to switch profile");
      }
    },
    [switchCompany, currentEvent?.id, isLeagueEvent, dispatch, user, switching],
  );

  const isStaging = () => {
    return import.meta.env.VITE_INS_ENV === "staging";
  };

  return (
    <div className="relative" ref={ref}>
      <div
        aria-expanded={open}
        aria-haspopup="true"
        className={cn(
          "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5",
          "transition-colors duration-150",
          "hover:bg-neutral-100 dark:hover:bg-neutral-800",
        )}
      >
        {/* Avatar */}
        <div
          onClick={() => {
            console.info("User data:", user);
            if (!user) return;
            // Determine redirect URL based on profile mode
            const isPersonal = personalSelected;
            if (isPersonal) {
              // Personal profile
              const url = isClubleaDomain()
                ? "https://auth.clublea.pro/profile"
                : isStaging()
                  ? "https://my-dev.synkd.life/profile"
                  : "https://my.synkd.life/profile";
              window.open(url, "_blank", "noopener noreferrer");
            } else {
              // Employee profile (company context)
              const companyId =
                user?.selectedCompanyMembership?.company?.id ||
                user?.company?.id;

              const employeeId =
                user?.selectedCompanyMembership?.employeeId || user?.id;
              const url = isClubleaDomain()
                ? `https://auth.clublea.pro/company/${companyId}/employee/me/${employeeId}`
                : isStaging()
                  ? `https://my-dev.synkd.life/company/${companyId}/employee/me/${employeeId}`
                  : `https://my.synkd.life/company/${companyId}/employee/me/${employeeId}`;
              window.open(url, "_blank", "noopener noreferrer");
            }
          }}
        >
          {menuAvatar ? (
            <img
              src={menuAvatar}
              alt=""
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ background: "var(--sidebar-bg)" }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Name + company */}
        <div
          onClick={() => setOpen((o) => !o)}
          className="hidden text-left sm:block"
        >
          <p
            className="text-sm font-semibold leading-tight"
            style={{
              color: "var(--event-font-colour, var(--event-header-title))",
            }}
          >
            {personalSelected ? "Personal Profile" : company?.name ?? "My Club"}
          </p>
        </div>

        <span
          onClick={() => setOpen((o) => !o)}
          className="text-neutral-400 dark:text-neutral-500"
        >
          <ChevronDownIcon />
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop — click outside to close */}
          <div
            className="fixed inset-0 z-[99]"
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              "absolute right-0 top-full z-[100] mt-1.5 w-52",
              "rounded-xl border border-neutral-200 bg-white py-1 shadow-modal",
              "dark:border-neutral-700 dark:bg-neutral-900",
              "animate-scale-in",
            )}
          >
            <div className="border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {user ? `${user.firstName} ${user.lastName}` : "Guest"}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">{user?.email}</p>
            </div>
            <div className="max-h-60 space-y-2 overflow-y-auto border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
              <ProfileSwitchOptions
                personalAvatar={personalAvatar}
                initials={initials}
                memberships={Array.isArray(user?.companies) ? user.companies : []}
                activeCompanyId={company?.id ?? null}
                personalSelected={personalSelected}
                personalAvailable={!!currentEvent?.id}
                switching={switching || isLeagueEvent}
                onSwitch={(companyId) => { void handleSwitchCompany(companyId); }}
              />
              {switchError && <p role="alert" className="text-xs text-red-500">{switchError}</p>}
            </div>
            <div className="py-1">
              {isAdmin && (
                <p
                  className="block cursor-pointer px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
                  onClick={() => {
                    setOpen(false);
                    void navigate("/admin/settings");
                  }}
                >
                  Settings
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────
const Topbar = memo(function Topbar() {
  const currentUser = useAppSelector((s) => s.user.currentUser);
  const [showQR, setShowQR] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const { toggle } = useSidebar();
  const navigate = useNavigate();

  const {
    currentEvent,
    openPlatformEventMembers,
    openEventContents,
    setEventLoading,
    setEventError,
  } = useEventsFeature();
  const title = currentEvent?.name_check ? (currentEvent?.name ?? "") : "";
  const {
    event: platformEventMembers,
    loading,
    error,
  } = usePlatformEventMembers(currentEvent?.id ?? "");
  const {
    event: eventContents,
    loading: loadingContents,
    error: errorContents,
  } = useAllEventContents(currentEvent?.id ?? "");
  const {
    data: notifications,
    loading: loadingNotifications,
    error: errorNotifications,
    refetch: refetchNotifications,
  } = useAllUserNotifications(currentEvent?.id ?? "");

  const userNotifications: UserNotification[] =
    (notifications?.userNotifications as UserNotification[] | undefined) ?? [];
  const notificationCount = userNotifications.filter(
    (n) => n.status !== "READ",
  ).length;

  useEffect(() => {
    setEventLoading(loading);
    openPlatformEventMembers(platformEventMembers);

    setEventLoading(loading); // to be reviewed for optimization
    if (error) setEventError(error?.message);
  }, [currentEvent?.id, platformEventMembers, openPlatformEventMembers]);

  useEffect(() => {
    setEventLoading(loadingContents);
    openEventContents(eventContents);

    setEventLoading(loadingContents); // to be reviewed for optimization
    if (errorContents) setEventError(errorContents?.message);
  }, [currentEvent?.id, eventContents, openEventContents]);

  const handleCart = useCallback(() => {
    void navigate(ROUTES.CART);
  }, [navigate]);

  const handleNotificationMarkedRead = useCallback(() => {
    void refetchNotifications?.();
  }, [refetchNotifications]);

  return (
    <header
      className={cn(
        "flex h-[var(--header-height)] shrink-0 items-center justify-between px-4 sm:px-6",
        "border-b border-neutral-200 bg-[var(--event-header-bg,#ffffff)]",
        "dark:border-neutral-800 dark:bg-[var(--event-header-bg,#171717)]",
      )}
      aria-label="Page header"
    >
      {/* ── Left: hamburger (mobile) + page title ── */}
      <div className="flex items-center gap-3">
        {/* Hamburger — only visible on mobile */}
        <button
          onClick={toggle}
          aria-label="Open navigation menu"
          style={{ color: "var(--event-font-colour, var(--event-header-title))" }}
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg lg:hidden",
            "transition-colors hover:bg-neutral-100/10",
          )}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path
              fillRule="evenodd"
              d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <h1
          className="hidden text-lg font-bold sm:text-xl md:block"
          style={{
            color: "var(--event-font-colour, var(--event-header-title))",
          }}
        >
          {title}
        </h1>
      </div>

      {/* ── Right: actions ── */}
      <div className="flex items-center gap-1">
        <ThemeToggle />
        <div className="relative" ref={notificationsRef}>
          <IconButton
            label={`Notifications${notificationCount > 0 ? ` (${notificationCount})` : ""}`}
            onClick={() => setShowNotifications((o) => !o)}
          >
            <BellIcon hasDot={notificationCount > 0} />
          </IconButton>
          {showNotifications && (
            <Suspense fallback={null}>
              <NotificationsDropdown
                notifications={userNotifications}
                loading={loadingNotifications}
                error={errorNotifications?.message ?? null}
                onClose={() => setShowNotifications(false)}
                onMarkedRead={handleNotificationMarkedRead}
              />
            </Suspense>
          )}
        </div>
        <IconButton label={"cart"} onClick={handleCart}>
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4 shrink-0"
          >
            <path d="M1 1.75A.75.75 0 0 1 1.75 1h1.628a1.75 1.75 0 0 1 1.734 1.51L5.18 3a65.25 65.25 0 0 1 13.36 1.412.75.75 0 0 1 .58.875 48.645 48.645 0 0 1-1.618 6.2.75.75 0 0 1-.712.513H6a2.503 2.503 0 0 0-2.292 1.5H17.25a.75.75 0 0 1 0 1.5H2.76a.75.75 0 0 1-.748-.807 4.002 4.002 0 0 1 2.716-3.486L3.626 2.716a.25.25 0 0 0-.248-.216H1.75A.75.75 0 0 1 1 1.75ZM6 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM15.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
          </svg>
        </IconButton>
        <IconButton label={"Scan profile"} onClick={() => setShowQR(!showQR)}>
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              className="lucide lucide-qr-code-icon lucide-qr-code"
            >
              <rect width="5" height="5" x="3" y="3" rx="1" />
              <rect width="5" height="5" x="16" y="3" rx="1" />
              <rect width="5" height="5" x="3" y="16" rx="1" />
              <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
              <path d="M21 21v.01" />
              <path d="M12 7v3a2 2 0 0 1-2 2H7" />
              <path d="M3 12h.01" />
              <path d="M12 3h.01" />
              <path d="M12 16v.01" />
              <path d="M16 12h1" />
              <path d="M21 12v.01" />
              <path d="M12 21v-1" />
            </svg>

            <Suspense fallback={null}>
              <ScanProfileModal
                open={showQR}
                onClose={() => setShowQR(false)}
                user={{
                  name: `${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`,
                  avatar: `${currentUser?.avatar ?? ""}`,
                  ...currentUser,
                }}
              />
            </Suspense>
          </>
        </IconButton>
        <div className="mx-1 h-6 w-px bg-neutral-200 dark:bg-neutral-700" />
        <UserMenu />
      </div>
    </header>
  );
});

export default Topbar;
